"use client";

import type {
	AiTimelineByIntentResponse,
	AiTimelineData,
} from "@saas/ai-analytics/lib/api";
import { useActiveProperty } from "@saas/properties/hooks/use-active-property";
import { ExportButton } from "@saas/shared/components/ExportButton";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@ui/components/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
} from "@ui/components/chart";
import { CHART_SERIES, chartSeriesByKey } from "@ui/lib/chart-palette";
import { TrendingUpIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

interface AiTrafficTimelineProps {
	data: AiTimelineData[];
	isLoading?: boolean;
	showDetailed?: boolean;
	filterInfo?: {
		timeRange?: string;
		pagePath?: string;
		aiModel?: string;
		country?: string;
		region?: string;
		city?: string;
		intents?: string[];
	};
	// New props for intent visualization
	intentData?: AiTimelineByIntentResponse;
	showByIntent?: boolean;
}

const BOT_VISITS_COLOR = CHART_SERIES[0];
const HUMAN_VISITS_COLOR = CHART_SERIES[1];

export function AiTrafficTimeline({
	data,
	isLoading,
	showDetailed = false,
	filterInfo,
	intentData,
	showByIntent = false,
}: AiTrafficTimelineProps) {
	const t = useTranslations("aiAnalytics.timeline");
	const { activeProperty } = useActiveProperty();

	if (isLoading) {
		return (
			<Card className={showDetailed ? "" : "lg:col-span-2"}>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<TrendingUpIcon className="h-5 w-5" />
						{t("title")}
					</CardTitle>
					<CardDescription>{t("description")}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="h-80 flex items-center justify-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!data || data.length === 0) {
		return (
			<Card className={showDetailed ? "" : "lg:col-span-2"}>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<TrendingUpIcon className="h-5 w-5" />
						{t("title")}
					</CardTitle>
					<CardDescription>{t("description")}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="h-80 flex items-center justify-center text-muted-foreground">
						{t("noData")}
					</div>
				</CardContent>
			</Card>
		);
	}

	// Determine which data to use and how to format it
	const useIntentData =
		showByIntent && intentData && intentData.timeline.length > 0;
	const sourceData = useIntentData ? intentData.timeline : data;

	// Format data for the chart
	const chartData = sourceData.map((item) => {
		// Parse the time bucket to create a readable label
		const date = new Date(item.time_bucket);
		const isHourly = item.time_bucket.includes("T");

		return {
			...item,
			date_label: isHourly
				? date.toLocaleDateString(undefined, {
						month: "short",
						day: "numeric",
						hour: "numeric",
					})
				: date.toLocaleDateString(undefined, {
						month: "short",
						day: "numeric",
					}),
			full_date: date,
		} as any; // Type assertion to handle dynamic intent keys
	});

	// Sort by date to ensure proper ordering
	chartData.sort((a, b) => a.full_date.getTime() - b.full_date.getTime());

	// Colour follows the intent, not its position in the currently-visible set:
	// assign slots from the full intent list the API returns, so filtering never
	// repaints the survivors. Intents past the 6 slots fold into the Other bucket.
	const intentColorByKey = chartSeriesByKey(
		intentData?.intents.map((intent) => intent.key) ?? [],
	);

	const chartConfig: ChartConfig = useIntentData
		? Object.fromEntries(
				(intentData?.intents ?? []).map((intent) => [
					intent.key,
					{
						label: intent.intent,
						color: intentColorByKey.get(intent.key),
					},
				]),
			)
		: {
				bot_visits: { label: t("botVisits"), color: BOT_VISITS_COLOR },
				human_visits: {
					label: t("humanVisits"),
					color: HUMAN_VISITS_COLOR,
				},
			};

	return (
		<Card className={showDetailed ? "" : "lg:col-span-2"}>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="flex items-center gap-2">
							<TrendingUpIcon className="h-5 w-5" />
							{showByIntent ? "AI Traffic by Intent" : t("title")}
						</CardTitle>
						<CardDescription>
							{showByIntent
								? "AI bot visits segmented by page intent. Pages with multiple intents contribute proportionally to each layer."
								: t("description")}
						</CardDescription>

						{/* Show explanation when intent filtering is active */}
						{filterInfo?.intents &&
							filterInfo.intents.length > 0 && (
								<div className="mt-2 p-2 bg-primary/10 rounded-md border border-primary/20">
									<div className="text-xs text-muted-foreground">
										<strong className="text-foreground">
											Intent Filter Active:
										</strong>{" "}
										Showing pages tagged with "
										{filterInfo.intents.join('", "')}"
										intent(s). Pages with multiple intents
										show their full visit count when they
										match the selected filter.
									</div>
								</div>
							)}
					</div>
					<ExportButton
						data={chartData}
						csvType="timeline"
						componentType="ai-traffic-timeline"
						propertyName={activeProperty?.domain ?? undefined}
						chartElementId="ai-traffic-timeline-chart"
						filterInfo={filterInfo}
					/>
				</div>
			</CardHeader>
			<CardContent>
				<div id="ai-traffic-timeline-chart">
					<ChartContainer
						config={chartConfig}
						className="h-80 w-full aspect-auto"
					>
						<AreaChart
							data={chartData}
							margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
						>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis
								dataKey="date_label"
								tickLine={false}
								axisLine={false}
								interval="preserveStartEnd"
							/>
							<YAxis tickLine={false} axisLine={false} />
							<Tooltip
								content={({ active, payload, label }) => {
									if (active && payload && payload.length) {
										const datum = payload[0].payload;
										return (
											<div className="bg-background border border-border rounded-lg p-3 shadow-lg">
												<p className="font-medium">
													{label}
												</p>
												<div className="space-y-1 mt-2">
													{useIntentData ? (
														// Intent-based tooltip
														<>
															{intentData?.intents.map(
																(intent) => {
																	const visits =
																		Number(
																			datum[
																				intent
																					.key
																			] ||
																				0,
																		);
																	if (
																		visits ===
																		0
																	)
																		return null;
																	return (
																		<div
																			key={
																				intent.key
																			}
																			className="flex items-center gap-2"
																		>
																			<div
																				className="w-3 h-3 rounded-full"
																				style={{
																					backgroundColor:
																						intentColorByKey.get(
																							intent.key,
																						),
																				}}
																			/>
																			<span className="text-sm">
																				{
																					intent.intent
																				}
																				:{" "}
																				{visits.toLocaleString()}
																			</span>
																		</div>
																	);
																},
															)}

															{/* Add explanation for intent distribution */}
															<div className="mt-2 pt-2 border-t border-border">
																<div className="text-xs text-muted-foreground">
																	{filterInfo?.intents &&
																	filterInfo
																		.intents
																		.length >
																		0
																		? "Filtered view: Pages show full visit count when matching selected intent(s)"
																		: "Stacked view: Multi-intent pages distribute visits proportionally across layers"}
																</div>
															</div>
														</>
													) : (
														// Regular tooltip
														<>
															<div className="flex items-center gap-2">
																<div
																	className="w-3 h-3 rounded-full"
																	style={{
																		backgroundColor:
																			BOT_VISITS_COLOR,
																	}}
																/>
																<span className="text-sm">
																	{t(
																		"botVisits",
																	)}
																	:{" "}
																	{Number(
																		datum.bot_visits ||
																			0,
																	).toLocaleString()}
																</span>
															</div>
															<div className="flex items-center gap-2">
																<div
																	className="w-3 h-3 rounded-full"
																	style={{
																		backgroundColor:
																			HUMAN_VISITS_COLOR,
																	}}
																/>
																<span className="text-sm">
																	{t(
																		"humanVisits",
																	)}
																	:{" "}
																	{Number(
																		datum.human_visits ||
																			0,
																	).toLocaleString()}
																</span>
															</div>
															<div className="flex items-center gap-2 text-muted-foreground">
																<span className="text-sm">
																	{t(
																		"humanVisitRatio",
																	)}
																	:{" "}
																	{Number(
																		datum.conversion_rate ||
																			0,
																	).toFixed(
																		1,
																	)}
																	%
																</span>
															</div>
														</>
													)}
												</div>
											</div>
										);
									}
									return null;
								}}
							/>
							<ChartLegend content={<ChartLegendContent />} />

							{useIntentData ? (
								// Render stacked areas for each intent
								intentData?.intents.map((intent) => (
									<Area
										key={intent.key}
										type="monotone"
										dataKey={intent.key}
										stackId="intents"
										stroke={intentColorByKey.get(
											intent.key,
										)}
										fill={intentColorByKey.get(intent.key)}
										fillOpacity={0.7}
										name={intent.intent}
									/>
								))
							) : (
								// Regular visualization — one axis; the human-visit ratio lives
								// in the tooltip and summary rather than on a second scale.
								<>
									<Area
										type="monotone"
										dataKey="bot_visits"
										stackId="1"
										stroke={BOT_VISITS_COLOR}
										fill={BOT_VISITS_COLOR}
										fillOpacity={0.3}
										name={t("botVisits")}
									/>
									<Area
										type="monotone"
										dataKey="human_visits"
										stackId="1"
										stroke={HUMAN_VISITS_COLOR}
										fill={HUMAN_VISITS_COLOR}
										fillOpacity={0.3}
										name={t("humanVisits")}
									/>
								</>
							)}
						</AreaChart>
					</ChartContainer>
				</div>

				{/* Summary stats */}
				<div className="mt-4 pt-4 border-t">
					{useIntentData ? (
						// Intent-based summary: show top intents with their totals
						<div className="space-y-4">
							<div className="text-sm font-medium text-muted-foreground">
								AI Traffic by Intent
								{filterInfo?.intents &&
									filterInfo.intents.length > 0 && (
										<span className="ml-2 text-primary">
											(Filtered by:{" "}
											{filterInfo.intents.join(", ")})
										</span>
									)}
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
								{intentData?.intents.map((intent) => {
									const total = chartData.reduce(
										(sum, item) =>
											sum + Number(item[intent.key] || 0),
										0,
									);
									if (total === 0) return null;

									return (
										<div
											key={intent.key}
											className="text-center"
										>
											<div className="text-2xl font-bold mb-1">
												{total.toLocaleString()}
											</div>
											<div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
												<span
													className="h-2 w-2 shrink-0 rounded-[2px]"
													style={{
														backgroundColor:
															intentColorByKey.get(
																intent.key,
															),
													}}
												/>
												{intent.intent}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					) : (
						// Regular summary stats
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<div className="text-center">
								<div className="text-2xl font-bold">
									{chartData
										.reduce(
											(sum, item) =>
												sum +
												Number(item.bot_visits || 0),
											0,
										)
										.toLocaleString()}
								</div>
								<div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
									<span
										className="h-2 w-2 shrink-0 rounded-[2px]"
										style={{
											backgroundColor: BOT_VISITS_COLOR,
										}}
									/>
									{t("totalBotVisits")}
								</div>
							</div>
							<div className="text-center">
								<div className="text-2xl font-bold">
									{chartData
										.reduce(
											(sum, item) =>
												sum +
												Number(item.human_visits || 0),
											0,
										)
										.toLocaleString()}
								</div>
								<div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
									<span
										className="h-2 w-2 shrink-0 rounded-[2px]"
										style={{
											backgroundColor: HUMAN_VISITS_COLOR,
										}}
									/>
									{t("totalHumanVisits")}
								</div>
							</div>
							<div className="text-center">
								<div className="text-2xl font-bold">
									{chartData.length > 0
										? (
												chartData.reduce(
													(sum, item) =>
														sum +
														Number(
															item.conversion_rate ||
																0,
														),
													0,
												) / chartData.length
											).toFixed(1)
										: "0.0"}
									%
								</div>
								<div className="text-sm text-muted-foreground">
									{t("avgHumanVisitRatio")}
								</div>
							</div>
						</div>
					)}

					{/* Matched Pages Section */}
					{useIntentData &&
						intentData?.matchedPages &&
						intentData.matchedPages.length > 0 && (
							<div className="mt-6 pt-6 border-t">
								<div className="text-sm font-medium text-muted-foreground mb-4">
									Pages Matching Selected Intent
									{filterInfo?.intents &&
									filterInfo.intents.length > 1
										? "s"
										: ""}
									{filterInfo?.intents &&
										filterInfo.intents.length > 0 && (
											<span className="ml-2 text-primary">
												({filterInfo.intents.join(", ")}
												)
											</span>
										)}
								</div>
								<div className="space-y-2 max-h-64 overflow-y-auto">
									{intentData.matchedPages.map(
										(page, idx) => (
											<div
												key={page.path}
												className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
											>
												<div className="flex-1 min-w-0">
													<div
														className="font-mono text-sm truncate"
														title={page.path}
													>
														{page.path}
													</div>
													<div className="text-xs text-muted-foreground mt-1">
														Intents:{" "}
														{page.intents.join(
															", ",
														)}
													</div>
												</div>
												<div className="flex items-center gap-3 ml-4">
													<div className="text-right">
														<div className="text-sm font-medium">
															{page.visits.toLocaleString()}
														</div>
														<div className="text-xs text-muted-foreground">
															visits
														</div>
													</div>
													<div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
														#{idx + 1}
													</div>
												</div>
											</div>
										),
									)}
								</div>
							</div>
						)}
				</div>
			</CardContent>
		</Card>
	);
}
